import React, { FC, useEffect, useState } from 'react'
import './Events.scss'

import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { ShowTGAlert } from 'Helpers/ShowTGAlert'
import profileService from 'Services/profileService'
import Swal from 'sweetalert2'
import eventService from 'Services/eventService'
import DatePicker from 'react-datepicker'
import ClockIcon from 'Assets/Images/clock.png'
import CalendarIcon from 'Assets/Images/calendar.png'
import DateInput from 'Components/DateInput/DateInput'
import { EventApi } from '@fullcalendar/core'

export type CreateEvent = {
  title: string
  description: string
  eventType: string
  location: string
  startDate: Date
  endDate: Date
  isRecurring: boolean
  frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly'
}

const fieldReq = 'Field is required'

const schema = yup.object({
  title: yup.string().required(fieldReq),
  description: yup.string().required(fieldReq),
  eventType: yup.string().required(fieldReq),
  location: yup.string().required(fieldReq),
  startDate: yup.date().required(fieldReq),
  endDate: yup
    .date()
    .min(yup.ref('startDate'), 'End date must be after start date')
    .required(fieldReq),
  isRecurring: yup.boolean().required(),
  frequency: yup
    .string()
    .oneOf(['daily', 'weekly', 'monthly', 'yearly'])
    .when('isRecurring', {
      is: true,
      then: (schema) => schema.required(fieldReq),
      otherwise: (schema) => schema.strip(),
    }),
})

interface propsType {
  eventToEdit?: EventApi | null
  onSuccess: () => void
}

const EventsComp: React.FC<propsType> = ({ eventToEdit, onSuccess }) => {
  const isEditMode = !!eventToEdit

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateEvent>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      description: '',
      eventType: '',
      location: '',
      startDate: new Date(),
      endDate: new Date(),
      isRecurring: false,
      frequency: undefined,
    },
  })

  const [request, setRequest] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  useEffect(() => {
    if (eventToEdit) {
      const ep = eventToEdit.extendedProps ?? {}
      const start = eventToEdit.start ? new Date(eventToEdit.start) : new Date()
      const end = eventToEdit.end ? new Date(eventToEdit.end) : new Date()

      reset({
        title: eventToEdit.title ?? '',
        description: ep.description ?? '',
        eventType: ep.eventType ?? '',
        location: ep.location ?? '',
        startDate: start,
        endDate: end,
        isRecurring: ep.isRecurring ?? false,
        frequency: ep.frequency ?? undefined,
      })

      setStartDate(start)
      setEndDate(end)
    } else {
      reset({
        title: '',
        description: '',
        eventType: '',
        location: '',
        startDate: new Date(),
        endDate: new Date(),
        isRecurring: false,
        frequency: undefined,
      })
      setStartDate(null)
      setEndDate(null)
    }
  }, [eventToEdit, reset])

  const handleStartChange = (date: Date | null) => {
    if (!date) return
    setStartDate(date)
    setValue('startDate', date)
  }

  const handleEndChange = (date: Date | null) => {
    if (!date) return
    setEndDate(date)
    setValue('endDate', date)
  }

  const onSubmit = handleSubmit(async (data) => {
    if (!data.startDate || !data.endDate) return
    setRequest(true)

    const payload = {
      title: data.title,
      description: data.description,
      eventType: data.eventType,
      location: data.location,
      startDate: data.startDate,
      endDate: data.endDate,
      isRecurring: data.isRecurring,
      ...(data.isRecurring && { frequency: data.frequency }),
    }

    try {
      let res: any
      if (isEditMode && eventToEdit?.id) {
        res = await eventService.updateEvent(eventToEdit.id, payload)
      } else {
        res = await eventService.createEvent(payload)
      }

      setRequest(false)
      // console.log('r', res)

      if (res?.data?.status === true) {
        await Swal.fire({
          text: isEditMode ? 'Event updated successfully' : 'Event created successfully',
          icon: 'success',
          confirmButtonText: 'Ok',
        })
        onSuccess()
      } else {
        showErrorAlert(res.data?.message ?? 'Something went wrong!')
      }
    } catch (error: any) {
      // console.log('e', error.res.data)
      setRequest(false)
      showErrorAlert(
        (error?.response?.data?.error || error?.response?.data?.message) ?? 'Something went wrong!',
      )
    }
  })

  const showErrorAlert = (text: string) => {
    Swal.mixin({
      customClass: { cancelButton: 'btn btn-danger' },
      buttonsStyling: false,
    }).fire({
      title: 'Oops!',
      text,
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: false,
      cancelButtonText: 'Close',
      reverseButtons: true,
    })
  }

  return (
    <div className='events'>
      <form onSubmit={onSubmit}>
        <div className='form-group'>
          <label>Title</label>
          <input
            autoComplete='off'
            type='text'
            className={`form-control ${errors.title ? 'is-invalid' : ''}`}
            {...register('title')}
          />
          {errors.title && <div className='invalid-feedback'>{errors.title.message}</div>}
        </div>

        <div className='form-group mt-3'>
          <label>Description</label>
          <input
            type='text'
            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
            {...register('description')}
          />
          {errors.description && (
            <div className='invalid-feedback'>{errors.description.message}</div>
          )}
        </div>

        <div className='form-group mt-3'>
          <label>Location</label>
          <input
            type='text'
            className={`form-control ${errors.location ? 'is-invalid' : ''}`}
            {...register('location')}
          />
          {errors.location && <div className='invalid-feedback'>{errors.location.message}</div>}
        </div>

        <div className='form-group mt-3'>
          <label>Event Type</label>
          <select
            className={`form-control ${errors.eventType ? 'is-invalid' : ''}`}
            {...register('eventType')}
          >
            <option value=''>Select type</option>
            <option value='birthday'>Birthday</option>
            <option value='work_anniversary'>Work Anniversary</option>
            <option value='knowledge_sharing'>Knowledge Sharing</option>
            <option value='management_retreat'>Management Retreat</option>
            <option value='review_meeting'>Review Meeting</option>
            <option value='others'>others</option>
          </select>
          {errors.eventType && <div className='invalid-feedback'>{errors.eventType.message}</div>}
        </div>

        <div className='form-group mt-3'>
          <label>Start Date</label>
          <DatePicker
            selected={startDate}
            onChange={handleStartChange}
            showTimeSelect
            dateFormat='MMMM d, yyyy h:mm aa'
            customInput={<DateInput error={errors.startDate} />}
          />
          {errors.startDate && (
            <div className='invalid-feedback d-block'>{errors.startDate.message}</div>
          )}
        </div>

        <div className='form-group mt-3'>
          <label>End Date</label>
          <DatePicker
            selected={endDate}
            onChange={handleEndChange}
            showTimeSelect
            minDate={startDate ?? undefined}
            dateFormat='MMMM d, yyyy h:mm aa'
            customInput={<DateInput error={errors.endDate} />}
          />
          {errors.endDate && (
            <div className='invalid-feedback d-block'>{errors.endDate.message}</div>
          )}
        </div>

        <div className='form-group mt-3'>
          <label>Recurring</label>
          <select
            className={`form-control ${errors.isRecurring ? 'is-invalid' : ''}`}
            {...register('isRecurring', { setValueAs: (v) => v === 'true' || v === true })}
          >
            <option value='false'>No</option>
            <option value='true'>Yes</option>
          </select>
          {errors.isRecurring && (
            <div className='invalid-feedback'>{errors.isRecurring.message}</div>
          )}
        </div>

        <div className='form-group mt-3'>
          <label>Frequency</label>
          <select
            className={`form-control ${errors.frequency ? 'is-invalid' : ''}`}
            {...register('frequency')}
          >
            <option value=''>Select frequency</option>
            <option value='daily'>Daily</option>
            <option value='weekly'>Weekly</option>
            <option value='monthly'>Monthly</option>
            <option value='yearly'>Yearly</option>
          </select>
          {errors.frequency && <div className='invalid-feedback'>{errors.frequency.message}</div>}
        </div>

        <div className='btns'>
          <div className='form-group mt-4 white'>
            <button
              type='button'
              className='btn btn-default-2 w-100'
              disabled={request}
              onClick={onSuccess}
            >
              Cancel
            </button>
          </div>
          <div className='form-group mt-4'>
            <button type='submit' className='btn btn-default-2 w-100' disabled={request}>
              {isEditMode ? 'Update Event' : 'Save Event'}
              {request && <span className='spinner-border spinner-border-sm ms-2' />}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default EventsComp
